import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "@xterm/xterm/css/xterm.css";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
/**
 * KTK-392 — locate is a poll, not a one-shot.
 *
 * `listSessions` resolves after a fixed 800 ms accumulation window with
 * whatever replies happened to land in it. An empty result therefore means
 * *either* "this agent has no session" *or* "nothing answered in time" —
 * a late host, a send queued while the relay socket was still pre-OPEN, a
 * reply that missed the window by 50 ms. Those are indistinguishable here.
 *
 * The old code treated the first empty window as final: it set `no-session`
 * and returned, with no retry and no timer. Correct frames kept arriving at
 * ~2/s and were ignored, so a momentary miss became a permanently wrong
 * terminal — which is why this read as "sometimes broken for weeks" rather
 * than as a brief race. Toggling agents wasn't a workaround, just a re-roll.
 *
 * So: retry while the evidence is self-contradictory. Hub's presence poll is
 * the independent witness — if it says the agent is live and locate says
 * there's no session, one of them is wrong and it's worth asking again.
 *
 * One fast retry, then river's 2s/4s/8s. The presence poll already broadcasts
 * `session:list` every 5 s and every host answers, so a tight loop would only
 * add duplicate frames to the pile the accumulator merges — hence the quick
 * backoff to a cap at/above the poll interval. The single 500 ms attempt is
 * kept deliberately: the common miss is a reply landing just past the 800 ms
 * window, and making the user watch a 2 s stall to recover from a 50 ms miss
 * trades one visible bug for a smaller one.
 */
const LOCATE_BACKOFF_MS = [500, 2_000, 4_000];
const LOCATE_BACKOFF_CAP_MS = 8_000;
/**
 * Attempts before settling on `no-session` when presence has *no opinion*
 * (`presenceLive === undefined`). With a live agent we retry indefinitely —
 * the state must self-heal without user action — but with no witness at all
 * an unbounded poll would hammer the relay for an agent that really is
 * stopped, so bound it and accept the answer.
 */
const LOCATE_ATTEMPTS_WITHOUT_PRESENCE = 4;
/**
 * AgentTerminal — renders xterm.js attached to the agent's Claude Code PTY
 * via the shell's `PtyAdapter`. Web's adapter routes through WorkspaceRelay
 * (`terminal:*` over WSS); Desktop's routes through the local node-pty
 * daemon via IPC. This component doesn't care which.
 */
export function AgentTerminal({ agentId, agentName, presenceLive }) {
    const ctx = useDataContext();
    const containerRef = useRef(null);
    const termRef = useRef(null);
    const fitRef = useRef(null);
    const attachmentRef = useRef(null);
    const [phase, setPhase] = useState({ kind: "locating", attempt: 0 });
    useEffect(() => {
        if (!containerRef.current)
            return;
        const container = containerRef.current;
        let cancelled = false;
        let cleanupAttachment = null;
        let term = null;
        (async () => {
            // xterm measures glyph width at `term.open()` against the currently
            // available font. If we boot before CaskaydiaCove is loaded, xterm's
            // geometry anchors to the fallback and the terminal renders with
            // powerline tofu + half-sized cells. Block on the FontFace API first.
            try {
                await document.fonts.load('14px "CaskaydiaCove NF Mono"');
            }
            catch {
                /* older browser / no FontFace API — fall through; the first render
                 * may be briefly off and snap once the font resolves. */
            }
            if (cancelled)
                return;
            const t = new Terminal({
                cursorBlink: true,
                fontSize: 14,
                fontFamily: "'CaskaydiaCove NF Mono', 'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                scrollback: 10000,
                theme: {
                    background: "#1a1b1e",
                    foreground: "#e1e1e3",
                    cursor: "#7c5cfc",
                    selectionBackground: "#7c5cfc44",
                    black: "#1a1b1e",
                    red: "#fa5252",
                    green: "#40c057",
                    yellow: "#fab005",
                    blue: "#7c5cfc",
                    magenta: "#be4bdb",
                    cyan: "#15aabf",
                    white: "#e1e1e3",
                },
            });
            const fit = new FitAddon();
            t.loadAddon(fit);
            t.open(container);
            fit.fit();
            term = t;
            termRef.current = t;
            fitRef.current = fit;
            // Locate the session for this agent. `listSessions` is a broadcast —
            // every connected host (pty daemon or agent runtime) answers, so one
            // call sees every session. The old per-host loop only worked while a
            // pty host existed to iterate (KTK-374): with daemons retired the
            // loop body never ran and every terminal showed "No running session".
            //
            // Retried rather than one-shot — see LOCATE_BACKOFF_MS above (KTK-392).
            let targetSessionId = null;
            for (let attempt = 0; !cancelled; attempt++) {
                if (attempt > 0)
                    setPhase({ kind: "locating", attempt });
                try {
                    const sessions = await ctx.shell.pty.listSessions();
                    if (cancelled)
                        return;
                    targetSessionId = sessions.find((s) => s.agentId === agentId)?.id ?? null;
                }
                catch (err) {
                    if (!cancelled) {
                        const message = err instanceof Error ? err.message : String(err);
                        setPhase({ kind: "error", message });
                        t.writeln(`\x1b[31mCouldn't list PTY hosts: ${message}\x1b[0m`);
                    }
                    return;
                }
                if (targetSessionId)
                    break;
                // Empty window. Whether to ask again depends on what Hub's presence
                // poll independently believes about this agent.
                if (presenceLive === false)
                    break; // agent really is stopped — believe it
                if (presenceLive === undefined && attempt + 1 >= LOCATE_ATTEMPTS_WITHOUT_PRESENCE) {
                    break; // no witness either way; stop asking and report what we found
                }
                // presenceLive === true: Hub says live, locate says absent. One of
                // them is wrong, so keep asking — this is the case that used to latch.
                const delay = LOCATE_BACKOFF_MS[attempt] ?? LOCATE_BACKOFF_CAP_MS;
                await new Promise((r) => setTimeout(r, delay));
            }
            if (cancelled)
                return;
            if (!targetSessionId) {
                setPhase({ kind: "no-session" });
                t.writeln(`\x1b[33mNo running session for agent ${agentName}. Start the agent with the button above.\x1b[0m`);
                return;
            }
            setPhase({ kind: "attaching", sessionId: targetSessionId });
            try {
                const attachment = await ctx.shell.pty.attach(targetSessionId, {
                    cols: t.cols,
                    rows: t.rows,
                    replayScrollback: true,
                });
                if (cancelled) {
                    await attachment.detach();
                    return;
                }
                attachmentRef.current = attachment;
                setPhase({ kind: "attached", sessionId: targetSessionId });
                t.onData((data) => {
                    void attachment.write(data);
                });
                const resizeObserver = new ResizeObserver(() => {
                    fit.fit();
                    void attachment.resize(t.cols, t.rows);
                });
                resizeObserver.observe(container);
                cleanupAttachment = async () => {
                    resizeObserver.disconnect();
                    await attachment.detach();
                };
                for await (const frame of attachment.frames) {
                    if (cancelled)
                        break;
                    if (frame.kind === "reset") {
                        t.reset();
                        t.clear();
                    }
                    else if (frame.kind === "data") {
                        t.write(new Uint8Array(frame.bytes));
                    }
                    else if (frame.kind === "exit") {
                        t.writeln("\r\n\x1b[33m[Session ended]\x1b[0m");
                    }
                }
            }
            catch (err) {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : String(err);
                    setPhase({ kind: "error", message });
                    t.writeln(`\x1b[31mAttach failed: ${message}\x1b[0m`);
                }
            }
        })();
        return () => {
            cancelled = true;
            void cleanupAttachment?.();
            term?.dispose();
            termRef.current = null;
            fitRef.current = null;
            attachmentRef.current = null;
        };
    }, [ctx, agentId, agentName, presenceLive]);
    const statusLabel = phase.kind === "locating"
        ? phase.attempt > 0
            ? // Say it's still trying rather than showing a frozen "locating…".
                // The retry is the fix for KTK-392; a user watching a silent spinner
                // can't tell it apart from the hang this replaces.
                `locating session… (retry ${phase.attempt})`
            : "locating session…"
        : phase.kind === "attaching"
            ? "attaching…"
            : phase.kind === "attached"
                ? ""
                : phase.kind === "no-session"
                    ? "no running session"
                    : phase.kind === "error"
                        ? `error: ${phase.message}`
                        : "";
    return (_jsxs("div", { style: { position: "relative", flex: 1, minHeight: 0 }, children: [_jsx("div", { ref: containerRef, style: {
                    position: "absolute",
                    inset: 0,
                    padding: 8,
                    background: "#1a1b1e",
                    overflow: "hidden",
                } }), statusLabel && (_jsx("div", { style: {
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontSize: 11,
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: "rgba(0,0,0,0.5)",
                    color: phase.kind === "error" ? "var(--error)" : "var(--text-muted)",
                    fontFamily: "'CaskaydiaCove NF Mono', 'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                }, children: statusLabel }))] }));
}
//# sourceMappingURL=AgentTerminal.js.map